import * as React from 'react';
import styled, { css, ThemeProps, DefaultTheme, useTheme } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { KEY_CODES } from '../../../utils/key-codes';
import { isHorizontal, isVertical, isLine, createTabStyleFragmentSelector, TabStyleFuncMap } from '../utils';
import { TabHeaderItemActionButton, ITabHeaderItemActionButtonProps } from './tab-header-item-action-button';
import { ITabItemInfoBase, ITabsPanelProps, TabStyle } from '../tab-types';
import { Typography } from '../../typography';
import { FlexBox } from '../../flex-box';
import { selectThemeSemantics } from '../../../styling';
import { StackPanel } from '../../stack-panel';

interface ITabHeaderBaseProps extends ITabItemInfoBase,
    Required<Pick<ITabsPanelProps, 'tabStyle' | 'tabOrientation' | 'tabWidth' | 'tabHeight'>>,
    Pick<ITabsPanelProps, | 'remoteHeaderContainerRef'> {
    isActive: boolean;
    isDisabled?: boolean;
    childCount: number;
    tabOverflow: boolean;
    isTabHeaderVisible: boolean;
    onClick(key: React.Key): void;
}

const TabHeaderContent = styled.span.attrs(() => ({
    className: String('ap-tabItem')
}))`
    width:  100%;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 1.2;
`;

const ActiveTypography = styled(Typography.Small)`
    font-weight: 500;
`;

interface ITabActonButtonRendererProps extends Exclude<ITabHeaderItemActionButtonProps, 'buttonColor'> {
    tabStyle: TabStyle;
    isActive?: boolean;
}

function TabActonButtonRenderer({ ref, tabStyle, isActive, ...rest }: ITabActonButtonRendererProps) {
    const theme = useTheme();
    const tabTheme = isLine(tabStyle) ? theme.tabsPanelLine : theme.tabsPanelConventional;
    const state = isActive ? tabTheme.colors.active : tabTheme.colors.default;
    return (
        <TabHeaderItemActionButton {...rest} isActive={isActive} buttonColor={state.tabItemText} />
    );
}

class TabHeaderItemBase extends React.Component<ITabHeaderBaseProps> {
    private itemRef = React.createRef<HTMLDivElement>();

    componentDidUpdate() {
        const { isTabHeaderVisible } = this.props;
        isTabHeaderVisible && this.scrollToView();
    }

    componentDidMount() {
        const { isTabHeaderVisible } = this.props;
        isTabHeaderVisible && this.scrollToView();
    }

    render() {
        const { header } = this.props;
        return header ? this.renderHeader() : null;
    }

    private renderHeader() {
        const { header, className, tabOrientation } = this.props;
        return (
            <FlexBox
                className={className}
                onClick={this._onClick}
                onKeyUp={this.onKeyUp}
                inline={isHorizontal(tabOrientation)} justifyContent="center" alignItems="center" flexDirection="row"
                ref={this.itemRef}
                title={header}
                tabIndex={0}>
                <StackPanel width="100%" alignment="center">
                    {this.renderHeaderContent()}
                    <>{this.renderActionButtons()}</>
                </StackPanel>
            </FlexBox>
        );
    }

    private scrollToView() {
        const { isActive } = this.props;
        const { current } = this.itemRef;
        if (!isActive || current === null) {
            return;
        }
        current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        });
    }

    private prepareHeader() {
        const { header, onRenderHeader, id, isActive } = this.props;
        const TypographyWrapper = (isActive) ? ActiveTypography : Typography.Small;
        return (onRenderHeader ?
            onRenderHeader({ id, header }) :
            <TypographyWrapper className="ap-tabItem-header">{header}</TypographyWrapper>
        );
    }

    private _onClick = () => {
        const { onClick, id, isDisabled } = this.props;
        if (isDisabled) {
            return;
        }
        onClick(id);
    }

    private onKeyUp = ({ key }: React.KeyboardEvent) => {
        if (key === KEY_CODES.enter) {
            this._onClick();
        }
    }

    private renderHeaderContent() {
        return (
            <TabHeaderContent>
                {this.prepareHeader()}
            </TabHeaderContent>
        );
    }

    private renderPrimaryActionButton() {
        const { primaryAction, tabStyle, isActive, id } = this.props;

        return primaryAction ? (
            <TabActonButtonRenderer
                tabKey={id}
                title={primaryAction.title}
                iconName={primaryAction.iconName}
                disabled={primaryAction.disableAction || false}
                tabStyle={tabStyle}
                isActive={isActive}
                onActionClick={primaryAction.onClick} />
        ) : null;
    }

    private renderContextMenuActionButton() {
        const { id, actions } = this.props;
        return actions && actions.length ? (
            <TabHeaderItemActionButton
                tabKey={id}
                iconName="ap-menu-round-vertical.outline" />
        ) : null;
    }

    private renderActionButtons() {
        const { primaryAction, actions } = this.props;
        return primaryAction || (actions && actions.length) ? (
            <ActionButtonWrapper>
                {this.renderPrimaryActionButton()}
                {this.renderContextMenuActionButton()}
            </ActionButtonWrapper>
        ) : null;
    }

}

const calculateTabWidth = memoizeFunction((props: ITabHeaderBaseProps) => {
    const { tabWidth, tabOrientation, childCount } = props;
    return isHorizontal(tabOrientation) ? (tabWidth ? tabWidth + 'px' : (100 / childCount) + '%') : 'auto';
});

const calculateTabHeight = memoizeFunction((props: ITabHeaderBaseProps) => {
    const { tabHeight, tabOrientation, childCount } = props;
    return isVertical(tabOrientation) ? (tabHeight ? tabHeight + 'px' : (100 / childCount) + '%') : '45px';
});

type TabStylesByType = TabStyleFuncMap<ITabHeaderBaseProps & ThemeProps<DefaultTheme>>;

const baseStyles: TabStylesByType = {
    base: memoizeFunction((theme, props) => css`
                min-width: 100px;
                box-sizing: border-box;
                position: relative;
                cursor: pointer;
                text-align: center;
                z-index: 0;
                background-color: ${theme.colors.default.tabItemBackground};
                width: ${calculateTabWidth(props)};
                height: ${calculateTabHeight(props)};
                padding: ${selectThemeSemantics(s => s.gap.l)};
                max-height: 100%;
                max-width: 100%;
            `),
    focussed: memoizeFunction((t) => css`
            &:focus {
                outline-color: ${t.colors.focussed.tabItemBorder};
            }
        `),
    active: memoizeFunction((t) => css`
            &:active {
                outline-color: ${t.colors.active.tabItemBorder};
            }
        `),
    baseDefaultOnly: memoizeFunction(() => css`
            
        `)
};

const conventionalStyles: TabStylesByType = {
    base: memoizeFunction((t, { tabOrientation }) => {
        const itemBorderRadius = t.composites.borderRadius.item;
        const vertical = isVertical(tabOrientation);
        return css`
            border-style: solid;
            border-width: ${t.composites.border.item};
            ${vertical && css`margin-top:  -${t.composites.border.item};`}
            ${!vertical && css`margin-left:  -${t.composites.border.item};`}

            &:first-child{
                ${vertical && css`margin-top: 0;`}
                ${!vertical && css`margin-left: 0;`}
                border-radius: ${itemBorderRadius} 0 0 0;
            }

            &:last-child{
                border-radius: ${vertical ? `0 0 0 ${itemBorderRadius}` : `0 ${itemBorderRadius} 0 0`};
            }
        `;
    }),
    default: memoizeFunction((t) => css`
            background-color: ${t.colors.default.tabItemBackground};
            color: ${t.colors.default.tabItemText};
            border-color: ${t.colors.default.tabItemBorder};
        `),
    disabled: memoizeFunction((t) => css`
            background-color: ${t.colors.disabled.tabItemBackground};
            color: ${t.colors.disabled.tabItemText};
            border-color: ${t.colors.disabled.tabItemBorder};
            cursor: not-allowed;
        `),
    active: memoizeFunction((t) => css`
            z-index: 1;
            border-color: ${t.colors.active.tabItemBorder};
            background-color: ${t.colors.active.tabItemBackground};
            color: ${t.colors.active.tabItemText};
            border-width: ${t.composites.border.item};
            border-color: ${t.colors.active.tabItemBorder};
            border-style: solid;
        `)
};

const lineStyles: TabStylesByType = {
    base: memoizeFunction(() => css`
            border-style: none;
        `),
    active: memoizeFunction((t, { tabOrientation }) => css`
            z-index: 1;
            border-color: ${t.colors.active.tabItemBorder};
            background-color: ${t.colors.active.tabItemBackground};
            color: ${t.colors.active.tabItemText};
            box-shadow: inset ${(
            isVertical(tabOrientation) ?
                `${t.colors.active.tabItemBorder} -${t.composites.border.item} 0 0 0` :
                `${t.colors.active.tabItemBorder} 0 -${t.composites.border.item} 0 0`)};
        `),
    disabled: memoizeFunction((t) => css`
            background-color: ${t.colors.disabled.tabItemBackground};
            color: ${t.colors.disabled.tabItemText};
            border-color: ${t.colors.disabled.tabItemBorder};
            cursor: not-allowed;
        `)
};

const baseStylesByType = {
    conventional: baseStyles,
    line: baseStyles
};

const styles = {
    conventional: conventionalStyles,
    line: lineStyles
};

const selectBaseTabStyleFragment = createTabStyleFragmentSelector<ITabHeaderBaseProps & ThemeProps<DefaultTheme>>(baseStylesByType);

const selectTabStyleFragment = createTabStyleFragmentSelector<ITabHeaderBaseProps & ThemeProps<DefaultTheme>>(styles);

const ActionButtonWrapper = styled.span.attrs(() => ({
    className: String('ap-tabsPanel-actionButtonWrapper')
}))`
    position: relative;
    top: 2px;
`;

export const TabHeaderItem = styled(TabHeaderItemBase).attrs(({ isActive }) => ({
    className: String(`ap-tabsPanel-tabItemWrapper ${isActive ? 'selected' : ''}`)
}))`
    ${selectBaseTabStyleFragment('base')}
    ${selectBaseTabStyleFragment('active')}
    ${selectBaseTabStyleFragment('focussed')}
    ${selectTabStyleFragment('base')}
    ${selectTabStyleFragment('default', p => !(p.isActive || p.isDisabled))}
    ${selectTabStyleFragment('active', p => p.isActive && !p.isDisabled)}
    ${selectBaseTabStyleFragment('baseDefaultOnly', p => !(p.isActive || p.isDisabled))}
    ${selectTabStyleFragment('disabled', p => !!p.isDisabled)}
`;
